import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  createPlan,
  planQuery,
  plansQuery,
  removePlan,
  savePlan,
  type PlanDay,
  type UsersShell,
} from '../../api/settingsUsers'
import { Forbidden } from '../../ui/Forbidden'
import { Modal } from '../../ui/Modal'
import { Spinner } from '../../ui/Spinner'
import { toast } from '../../ui/notify'
import { failed, WEEK_DAYS } from './shared'

/**
 * Work Hour Plans - `work_hours/plans/index.php` (cadru + modala "Create plan"),
 * `plans/list.php` (lista) si `plans/edit_form.php` (formularul care se deschide
 * SUB randul planului, ca in portal).
 *
 * Un plan are sapte zile; fiecare zi are o bifa de pornit, doua ore, si o a
 * doua pereche de ore care apare numai cand e bifat "Night plan" - exact ca in
 * machete.
 *
 * `work_hours/user_plans/index.php` e un FISIER GOL in portal (zero randuri),
 * si metoda `user_plans()` doar il incarca: nu e nimic de mutat acolo.
 */

const EMPTY_DAY: PlanDay = {
  enabled: false,
  startHour: '',
  endHour: '',
  startHourExtended: '',
  endHourExtended: '',
  nightPlan: false,
}

type DayMap = Record<number, PlanDay>

function emptyWeek(): DayMap {
  const week: DayMap = {}
  for (const { day } of WEEK_DAYS) week[day] = { ...EMPTY_DAY }
  return week
}

/** Cele sapte zile, in forma pe care o asteapta capatul (`day[i][...]`). */
function dayFields(week: DayMap): Record<string, string> {
  const fields: Record<string, string> = {}
  for (const { day } of WEEK_DAYS) {
    const value = week[day] ?? EMPTY_DAY
    if (value.enabled) fields[`day[${day}][enabled]`] = 'on'
    fields[`day[${day}][start_hour]`] = value.startHour
    fields[`day[${day}][end_hour]`] = value.endHour
    fields[`day[${day}][start_hour_extended]`] = value.startHourExtended
    fields[`day[${day}][end_hour_extended]`] = value.endHourExtended
    if (value.nightPlan) fields[`day[${day}][night_plan]`] = 'on'
  }
  return fields
}

export function PlansPanel({ shell }: { shell: UsersShell }) {
  const client = useQueryClient()
  const page = useQuery(plansQuery)
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(0)

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (!page.data) return <Spinner />

  const reload = () => void client.invalidateQueries({ queryKey: ['settings', 'users', 'plans'] })

  const drop = (id: number) => {
    if (!confirm('Remove this plan?')) return
    void removePlan(id)
      .then(() => {
        toast('Plan removed', 'Success')
        if (editing === id) setEditing(0)
        reload()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
  }

  return (
    <div id="content">
      <h4>Work hours plans</h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage work hours <strong>plans</strong>
      </label>
      {shell.may['settings.user_accounts.edit'] ? (
        <button className="btn btn-success pull-right" type="button" onClick={() => setAdding(true)}>
          <i className="fas fa-plus" aria-hidden="true"></i> Create plan
        </button>
      ) : null}
      <hr />

      <div className="list-group">
        {page.data.plans.length > 0 ? (
          <ul style={{ paddingLeft: 0, listStyle: 'none' }}>
            {page.data.plans.map((plan) => (
              <li
                className="list-li"
                key={plan.id}
                style={{
                  padding: '10px 20px',
                  marginBottom: 4,
                  borderRadius: 4,
                  border: '1px solid #ddd',
                  borderLeft: '6px solid #DDD',
                  borderRight: '6px solid #DDD',
                }}
              >
                <div className="row">
                  <div className="col-md-8">
                    <h6 style={{ margin: 0, padding: '5px 0' }}>
                      <i className="far fa-calendar-check" aria-hidden="true"></i> {plan.name}
                    </h6>
                  </div>
                  <div className="col-md-4">
                    <button
                      className="btn btn-danger btn-xs pull-right"
                      type="button"
                      style={{ marginLeft: 20 }}
                      onClick={() => drop(plan.id)}
                    >
                      <i className="fas fa-trash" aria-hidden="true"></i> Remove
                    </button>
                    <button
                      className="btn btn-primary btn-xs pull-right editPlan"
                      type="button"
                      onClick={() => setEditing(editing === plan.id ? 0 : plan.id)}
                    >
                      <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                    </button>
                  </div>
                </div>
                {editing === plan.id ? (
                  <div className="row">
                    <EditPlanForm
                      planId={plan.id}
                      onClose={() => setEditing(0)}
                      onSaved={() => {
                        setEditing(0)
                        reload()
                      }}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="alert alert-info">
            <i className="fas fa-exclamation-circle" aria-hidden="true"></i> No plans founded.
          </div>
        )}
      </div>

      <NewPlanModal
        open={adding}
        onClose={() => setAdding(false)}
        onSaved={() => {
          setAdding(false)
          reload()
        }}
      />
    </div>
  )
}

/** Cele sapte randuri de zile, folosite si la creare si la editare. */
function WeekFields({ week, onChange }: { week: DayMap; onChange(next: DayMap): void }) {
  const set = (day: number, patch: Partial<PlanDay>) => onChange({ ...week, [day]: { ...week[day], ...patch } })

  return (
    <>
      {WEEK_DAYS.map(({ day, label }) => {
        const value = week[day] ?? EMPTY_DAY
        return (
          <div className="form-group" key={day}>
            <div className="row">
              <div className="col-md-3">
                <input
                  className="dayCheck"
                  type="checkbox"
                  checked={value.enabled}
                  onChange={(e) => set(day, { enabled: e.target.checked })}
                />{' '}
                <label className="font-12">{label}</label>
              </div>
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-addon font-12">from</span>
                  <input
                    className="form-control input-sm"
                    type="time"
                    value={value.startHour}
                    onChange={(e) => set(day, { startHour: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="input-group">
                  <span className="input-group-addon font-12">to</span>
                  <input
                    className="form-control input-sm"
                    type="time"
                    value={value.endHour}
                    onChange={(e) => set(day, { endHour: e.target.value })}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <label className="font-12">Night plan</label>{' '}
                <input
                  className="nightCheck"
                  type="checkbox"
                  checked={value.nightPlan}
                  onChange={(e) => set(day, { nightPlan: e.target.checked })}
                />
              </div>
            </div>
            {value.nightPlan ? (
              <div className="row" style={{ marginTop: 10 }}>
                <div className="col-md-offset-3 col-md-3">
                  <div className="input-group">
                    <span className="input-group-addon font-12">from</span>
                    <input
                      className="form-control input-sm"
                      type="time"
                      value={value.startHourExtended}
                      onChange={(e) => set(day, { startHourExtended: e.target.value })}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="input-group">
                    <span className="input-group-addon font-12">to</span>
                    <input
                      className="form-control input-sm"
                      type="time"
                      value={value.endHourExtended}
                      onChange={(e) => set(day, { endHourExtended: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ) : null}
            <hr className="dotted" />
          </div>
        )
      })}
    </>
  )
}

function NewPlanModal({ open, onClose, onSaved }: { open: boolean; onClose(): void; onSaved(): void }) {
  const [name, setName] = useState('')
  const [week, setWeek] = useState<DayMap>(emptyWeek)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!open) return
    setName('')
    setWeek(emptyWeek())
  }, [open])

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void createPlan({ plan_name: name, ...dayFields(week) })
      .then(() => {
        toast('Plan created', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <Modal id="newPlanModal" open={open} onClose={onClose} title="Create new plan" labelledBy="newPlanModalLabel">
      <form method="post" id="create-plan-form" onSubmit={submit}>
        <div className="form-group">
          <label className="control-label">
            Plan Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control input-sm"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <WeekFields week={week} onChange={setWeek} />
        <div className="form-group aright">
          <button type="button" className="btn btn-default" data-dismiss="modal" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

function EditPlanForm({ planId, onClose, onSaved }: { planId: number; onClose(): void; onSaved(): void }) {
  const page = useQuery(planQuery(planId))
  const [name, setName] = useState('')
  const [week, setWeek] = useState<DayMap>(emptyWeek)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!page.data) return
    setName(page.data.plan.name)
    const next = emptyWeek()
    for (const [day, value] of Object.entries(page.data.days)) next[Number(day)] = value
    setWeek(next)
  }, [page.data])

  if (page.error) return <div className="alert alert-danger">{failed(page.error)}</div>
  if (!page.data) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    void savePlan(planId, { plan_name: name, ...dayFields(week) })
      .then(() => {
        toast('Plan updated', 'Success')
        onSaved()
      })
      .catch((error: unknown) => toast(failed(error), 'Error', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div className="col-md-12 edit-form-container" style={{ marginTop: 20 }}>
      <form method="post" id="update-plan-form" onSubmit={submit}>
        <div className="form-group">
          <label className="control-label">
            Plan Name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control input-sm"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <WeekFields week={week} onChange={setWeek} />
        <div className="form-group aright">
          <button type="button" className="btn btn-default close-form" onClick={onClose}>
            Close
          </button>{' '}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Saving...' : 'Update'}
          </button>
        </div>
      </form>
    </div>
  )
}
